import React, { useState, useEffect, useRef } from "react";
import { REGEX } from "constants/CommonConstants";
import { StaticCanvas, Image as FabricImage, Line, Textbox, Text } from "fabric";

export const mapBackendTemplateToFrontend = (item) => {
  let t = item.template || {};
  if (typeof item.template === "string") {
    try {
      t = JSON.parse(item.template);
      if (typeof t === "string") {
        t = JSON.parse(t);
      }
    } catch (_error) {
      t = {};
    }
  }
  const canvasSizeObj = t.CanvasSize || t.canvasSize || {};
  
  return {
    id: item.ID || item.id,
    name: item.name || t.Name || item.Name,
    templateType: item.type || t.TemplateType || t.templateType,
    previewImage: item.bg_path || t.PreviewImage || t.previewImage || t.backgroundImageSrc || t.BackgroundImageSrc,
    canvasSize: {
      width: canvasSizeObj.width || canvasSizeObj.Width || 900,
      height: canvasSizeObj.height || canvasSizeObj.Height || 600
    },
    backgroundType: t.BackgroundType || t.backgroundType,
    backgroundImageSrc: t.BackgroundImageSrc || t.backgroundImageSrc || item.bg_path,
    layout: (t.Layout || t.layout || []).map(l => {
      const type = l.Type ?? l.type;
      
      let extraStyles = {};
      try {
        let srcStr = l.Src ?? l.src;
        if (srcStr) {
          const jsonStart = srcStr.indexOf('{');
          if (jsonStart !== -1) {
            srcStr = srcStr.substring(jsonStart);
            extraStyles = JSON.parse(srcStr);
          }
        }
      } catch (_error) {
        extraStyles = {};
      }

      let mapped = {
        type: type,
        text: l.Text ?? l.text,
        x: l.X ?? l.x,
        y: l.Y ?? l.y,
        width: l.Width ?? l.width,
        height: l.Height ?? l.height,
        fontSize: l.FontSize ?? l.fontSize,
        fontFamily: l.FontFamily ?? l.fontFamily,
        fontWeight: extraStyles.fontWeight || l.FontWeight || l.fontWeight,
        fontStyle: extraStyles.fontStyle || l.FontStyle || l.fontStyle,
        underline: extraStyles.underline !== undefined ? extraStyles.underline : (l.Underline !== undefined ? l.Underline : l.underline),
        styles: extraStyles.styles || l.Styles || l.styles || {},
        color: l.Color ?? l.color,
        angle: l.Angle ?? l.angle,
        textAlign: l.TextAlign ?? l.textAlign,
        originX: l.OriginX ?? l.originX,
        originY: l.OriginY ?? l.originY,
        shapeType: l.ShapeType ?? l.shapeType,
        stroke: l.Stroke ?? l.stroke,
        fill: l.Fill ?? l.fill,
        strokeWidth: l.StrokeWidth ?? l.strokeWidth,
        radius: l.Radius ?? l.radius,
        rx: l.Rx ?? l.rx,
        ry: l.Ry ?? l.ry,
        x1: l.X1 ?? l.x1,
        y1: l.Y1 ?? l.y1,
        x2: l.X2 ?? l.x2,
        y2: l.Y2 ?? l.y2,
        src: l.Src ?? l.src,
        scaleX: l.ScaleX ?? l.scaleX,
        scaleY: l.ScaleY ?? l.scaleY,
        lineHeight: l.LineHeight ?? l.lineHeight ?? 1,
        signatureKey: l.SignatureKey ?? l.signatureKey,
        isPlaceholder: l.IsPlaceholder ?? l.isPlaceholder,
        imageId: l.ImageID ?? l.imageId ?? l.ImageId,
      };

      if (type?.toLowerCase() === "shape" && (l.Text || l.text)) {
        try {
          const textStr = l.Text || l.text;
          const shapeData = JSON.parse(textStr);
          mapped = { ...mapped, ...shapeData };
        } catch (_error) {
          mapped.text = l.Text || l.text;
        }
      }

      return mapped;
    })
  };
};


export const replaceInlinePlaceholdersAndStyles = (text, originalStyles, values) => {
  if (!text) return { value: "", styles: {} };

  const lines = text.split('\n');
  const newStyles = {};
  const newLines = [];

  lines.forEach((lineText, lineIdx) => {
    let newLine = "";
    newStyles[lineIdx] = {};
    let newCharIdx = 0;

    const regex = new RegExp(REGEX.INLINE_PLACEHOLDER.source, 'g');
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(lineText)) !== null) {
      const matchStart = match.index;
      const matchLength = match[0].length;
      const key = match[1];
      const replacement = values[key] ?? match[0];

      for (let i = lastIndex; i < matchStart; i++) {
        newLine += lineText[i];
        if (originalStyles?.[lineIdx]?.[i]) {
          newStyles[lineIdx][newCharIdx] = { ...originalStyles[lineIdx][i] };
        }
        newCharIdx++;
      }

      const replacementStyle = originalStyles?.[lineIdx]?.[matchStart] || {};
      for (let i = 0; i < replacement.length; i++) {
        newLine += replacement[i];
        if (Object.keys(replacementStyle).length > 0) {
          newStyles[lineIdx][newCharIdx] = { ...replacementStyle };
        }
        newCharIdx++;
      }

      lastIndex = matchStart + matchLength;
    }

    for (let i = lastIndex; i < lineText.length; i++) {
      newLine += lineText[i];
      if (originalStyles?.[lineIdx]?.[i]) {
        newStyles[lineIdx][newCharIdx] = { ...originalStyles[lineIdx][i] };
      }
      newCharIdx++;
    }

    newLines.push(newLine);
  });

  return {
    value: newLines.join('\n'),
    styles: newStyles
  };
};

const renderTextWithStyles = (text, layoutItem, extraStyles) => {
  if (!text) return null;
  const globalFontWeight = extraStyles.fontWeight || layoutItem.fontWeight || "normal";
  const globalFontStyle = extraStyles.fontStyle || layoutItem.fontStyle || "normal";
  const globalUnderline = extraStyles.underline !== undefined ? extraStyles.underline : layoutItem.underline;
  const charStyles = layoutItem.styles || extraStyles.styles || {};

  return text.split('\n').map((lineText, lineIdx) => {
    const chunks = [];
    let currentChunk = { text: '', styles: {} };

    for (let i = 0; i < lineText.length; i++) {
      const styleObj = charStyles?.[lineIdx]?.[i] || {};
      const stylesKey = JSON.stringify({
        fontWeight: styleObj.fontWeight || globalFontWeight,
        fontStyle: styleObj.fontStyle || globalFontStyle,
        underline: styleObj.underline !== undefined ? styleObj.underline : globalUnderline
      });

      if (currentChunk.text === '') {
        currentChunk.styles = JSON.parse(stylesKey);
        currentChunk.text += lineText[i];
        currentChunk.stylesKey = stylesKey;
      } else if (currentChunk.stylesKey === stylesKey) {
        currentChunk.text += lineText[i];
      } else {
        chunks.push({ ...currentChunk });
        currentChunk = {
          text: lineText[i],
          styles: JSON.parse(stylesKey),
          stylesKey: stylesKey
        };
      }
    }
    if (currentChunk.text) {
      chunks.push(currentChunk);
    }

    return (
      <div key={lineIdx} className="template-preview-text-line">
        {chunks.map((chunk, idx) => (
          <span key={idx} style={{
            fontWeight: chunk.styles.fontWeight,
            fontStyle: chunk.styles.fontStyle,
            textDecoration: chunk.styles.underline ? "underline" : "none"
          }}>
            {chunk.text}
          </span>
        ))}
      </div>
    );
  });
};

export const ThumbnailPreview = ({ parsedTemplate, values = null }) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  const width = parsedTemplate?.canvasSize?.width || parsedTemplate?.CanvasSize?.Width || parsedTemplate?.CanvasSize?.width || 900;
  const height = parsedTemplate?.canvasSize?.height || parsedTemplate?.CanvasSize?.Height || parsedTemplate?.CanvasSize?.height || 600;

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setScale(entry.contentRect.width / width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [width]);

  return (
    <div ref={containerRef} className="template-preview-container" style={{ aspectRatio: `${width} / ${height}` }}>
      <div
        className="template-preview-canvas"
        style={{
          width: `${width / 16}rem`,
          height: `${height / 16}rem`,
          transform: `scale(${scale})`,
          backgroundImage: `url(${parsedTemplate?.previewImage || "/certificate-bg.png"})`,
          backgroundSize: "100% 100%"
        }}
      >
        {parsedTemplate?.layout?.map((layoutItem, idx) => {
          if (layoutItem.type === "text") {
            let extraStyles = {};
            try {
              let srcStr = layoutItem.Src ?? layoutItem.src;
              if (srcStr) {
                const jsonStart = srcStr.indexOf('{');
                if (jsonStart !== -1) {
                  srcStr = srcStr.substring(jsonStart);
                  extraStyles = JSON.parse(srcStr);
                }
              }
            } catch (e) {}

            let renderText = layoutItem.text;
            let renderStyles = extraStyles;
            
            if (values) {
              const replaced = replaceInlinePlaceholdersAndStyles(
                layoutItem.text,
                extraStyles?.styles || layoutItem.styles || {},
                values
              );
              renderText = replaced.value;
              renderStyles = { ...extraStyles, styles: replaced.styles };
            }

            return (
              <div
                key={idx}
                className="template-preview-text"
                style={{
                  left: `${layoutItem.x}%`,
                  top: `${layoutItem.y}%`,
                  transform: `translate(${(layoutItem.originX || "center") === "left" ? "0%" : layoutItem.originX === "right" ? "-100%" : "-50%"}, ${(layoutItem.originY || "center") === "top" ? "0%" : layoutItem.originY === "bottom" ? "-100%" : "-50%"})` + (layoutItem.angle ? ` rotate(${layoutItem.angle}deg)` : ""),
                  transformOrigin: `${(layoutItem.originX || "center") === "left" ? "0%" : layoutItem.originX === "right" ? "100%" : "50%"} ${(layoutItem.originY || "center") === "top" ? "0%" : layoutItem.originY === "bottom" ? "100%" : "50%"}`,
                  textAlign: layoutItem.textAlign || "left",
                  textAlignLast: (layoutItem.textAlign === "justify" || layoutItem.TextAlign === "justify") ? "justify" : "auto",
                  width: layoutItem.width ? `${(layoutItem.width / width) * 100}%` : "auto",
                  fontSize: `${(layoutItem.fontSize || 16) / 16}rem`,
                  fontFamily: layoutItem.fontFamily || "Arial",
                  color: layoutItem.color || "#000",
                }}
              >
                {renderTextWithStyles(renderText, layoutItem, renderStyles)}
              </div>
            );
          }

          if (layoutItem.type === "signature" || layoutItem.type === "image") {
            if (!layoutItem.src) return null;
            const rotate = layoutItem.angle ? ` rotate(${layoutItem.angle}deg)` : "";
            return (
              <img
                key={idx}
                src={layoutItem.src}
                alt={layoutItem.type}
                className="template-preview-image"
                style={{
                  left: `${layoutItem.x}%`,
                  top: `${layoutItem.y}%`,
                  transform: `translate(${(layoutItem.originX || "left") === "left" ? "0%" : layoutItem.originX === "right" ? "-100%" : "-50%"}, ${(layoutItem.originY || "top") === "top" ? "0%" : layoutItem.originY === "bottom" ? "-100%" : "-50%"})` + rotate,
                  transformOrigin: `${(layoutItem.originX || "left") === "left" ? "0%" : layoutItem.originX === "right" ? "100%" : "50%"} ${(layoutItem.originY || "top") === "top" ? "0%" : layoutItem.originY === "bottom" ? "100%" : "50%"}`,
                  width: `${layoutItem.width ? (layoutItem.width / width) * 100 : (layoutItem.scaleX || 0.5) * 32}%`,
                  height: layoutItem.height ? `${(layoutItem.height / height) * 100}%` : undefined,
                }}
              />
            );
          }

          if (layoutItem.type?.toLowerCase() === "shape") {
            const getTranslate = () => {
              const ox = layoutItem.originX || "left";
              const oy = layoutItem.originY || "top";
              const tx = ox === "left" ? "0%" : ox === "right" ? "-100%" : "-50%";
              const ty = oy === "top" ? "0%" : oy === "bottom" ? "-100%" : "-50%";
              return `translate(${tx}, ${ty})`;
            };
            const getTransformOrigin = () => {
              const ox = layoutItem.originX || "left";
              const oy = layoutItem.originY || "top";
              const tx = ox === "left" ? "0%" : ox === "right" ? "100%" : "50%";
              const ty = oy === "top" ? "0%" : oy === "bottom" ? "100%" : "50%";
              return `${tx} ${ty}`;
            };

            const rotate = layoutItem.angle ? ` rotate(${layoutItem.angle}deg)` : "";

            if (layoutItem.shapeType === "line") {
              const lineThickness = layoutItem.strokeWidth !== undefined ? layoutItem.strokeWidth : 2;
              const unscaledWidth = layoutItem.width || 200;
              const ox = layoutItem.originX || "center";
              const oy = layoutItem.originY || "center";
              const tx = ox === "left" ? "0%" : ox === "right" ? "-100%" : "-50%";
              const ty = oy === "top" ? "0%" : oy === "bottom" ? "-100%" : "-50%";
              const transformOriginX = ox === "left" ? "0%" : ox === "right" ? "100%" : "50%";
              const transformOriginY = oy === "top" ? "0%" : oy === "bottom" ? "100%" : "50%";
              return (
                <div
                  key={idx}
                  className="template-preview-shape"
                  style={{
                    left: `${layoutItem.x}%`,
                    top: `${layoutItem.y}%`,
                    transform: `translate(${tx}, ${ty})${rotate}`,
                    transformOrigin: `${transformOriginX} ${transformOriginY}`,
                    width: `${((unscaledWidth * (layoutItem.scaleX || 1)) / width) * 100}%`,
                    height: `${(lineThickness * (layoutItem.scaleY || 1)) / 16}rem`,
                    backgroundColor: layoutItem.stroke || "#000",
                  }}
                />
              );
            }

            let shapeWidth = 100;
            let shapeHeight = 100;
            if (layoutItem.shapeType === "circle") {
              shapeWidth = (layoutItem.radius || 50) * 2;
              shapeHeight = (layoutItem.radius || 50) * 2;
            } else if (layoutItem.shapeType === "ellipse") {
              shapeWidth = (layoutItem.rx || 50) * 2;
              shapeHeight = (layoutItem.ry || 50) * 2;
            } else {
              shapeWidth = layoutItem.width || 100;
              shapeHeight = layoutItem.height || 100;
            }

            const finalWidth = Math.max(shapeWidth * (layoutItem.scaleX || 1), 1);
            const finalHeight = Math.max(shapeHeight * (layoutItem.scaleY || 1), 1);
            const strokeDasharray = layoutItem.dash?.length ? layoutItem.dash.join(',') : undefined;

            return (
              <div
                key={idx}
                className="template-preview-shape"
                style={{
                  left: `${layoutItem.x}%`,
                  top: `${layoutItem.y}%`,
                  transform: getTranslate() + rotate,
                  transformOrigin: getTransformOrigin(),
                  width: `${(finalWidth / width) * 100}%`,
                  height: `${(finalHeight / height) * 100}%`,
                }}
              >
                <svg width="100%" height="100%" viewBox={`0 0 ${shapeWidth} ${shapeHeight}`} preserveAspectRatio="none">
                  {layoutItem.shapeType === "rect" && (
                    <rect x={0} y={0} width={shapeWidth} height={shapeHeight} fill={layoutItem.fill || "transparent"} stroke={layoutItem.stroke || "#000"} strokeWidth={layoutItem.strokeWidth !== undefined ? layoutItem.strokeWidth : 2} strokeDasharray={strokeDasharray} />
                  )}
                  {(layoutItem.shapeType === "circle" || layoutItem.shapeType === "ellipse") && (
                    <ellipse cx={shapeWidth / 2} cy={shapeHeight / 2} rx={shapeWidth / 2} ry={shapeHeight / 2} fill={layoutItem.fill || "transparent"} stroke={layoutItem.stroke || "#000"} strokeWidth={layoutItem.strokeWidth !== undefined ? layoutItem.strokeWidth : 2} strokeDasharray={strokeDasharray} />
                  )}
                  {layoutItem.shapeType === "triangle" && (
                    <polygon points={`${shapeWidth / 2},0 ${shapeWidth},${shapeHeight} 0,${shapeHeight}`} fill={layoutItem.fill || "transparent"} stroke={layoutItem.stroke || "#000"} strokeWidth={layoutItem.strokeWidth !== undefined ? layoutItem.strokeWidth : 2} strokeDasharray={strokeDasharray} />
                  )}
                </svg>
              </div>
            );
          }

          return null;
        })}
      </div>
    </div>
  );
};

export const downloadImage = async (imageUrl, fileName = "image.jpg") => {
  try {
    let fetchUrl = imageUrl;
    if (imageUrl && !imageUrl.startsWith("data:")) {
      try {
        const urlObj = new URL(imageUrl);
        urlObj.searchParams.set("t", Date.now());
        fetchUrl = urlObj.toString();
      } catch (error) {
        fetchUrl = imageUrl + (imageUrl.includes("?") ? "&" : "?") + `t=${Date.now()}`;
      }
    }

    const response = await fetch(fetchUrl, { mode: "cors" });
    if (!response.ok) throw new Error("Network response was not ok");
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const generateCertificateFile = async (parsedTemplate, fileName = "Certificate.png") => {
  if (!parsedTemplate) return null;

  const { width, height } = parsedTemplate.canvasSize || {
    width: parsedTemplate.CanvasSize?.Width || 707,
    height: parsedTemplate.CanvasSize?.Height || 500,
  };

  const hiddenCanvasElement = document.createElement("canvas");
  hiddenCanvasElement.width = width;
  hiddenCanvasElement.height = height;

  const canvas = new StaticCanvas(hiddenCanvasElement, {
    width,
    height,
    selection: false,
  });

  try {
    const getCorsUrl = (url) => {
      if (!url || url.startsWith("data:")) return url;
      try {
        const urlObj = new URL(url);
        urlObj.searchParams.set("t", Date.now());
        return urlObj.toString();
      } catch (error) {
        return url + (url.includes("?") ? "&" : "?") + `t=${Date.now()}`;
      }
    };

    if (parsedTemplate.previewImage) {
      const backgroundUrl = getCorsUrl(parsedTemplate.previewImage);
      const bg = await FabricImage.fromURL(backgroundUrl, { crossOrigin: "anonymous" });
      bg.set({ 
        left: 0, 
        top: 0, 
        originX: "left", 
        originY: "top", 
        selectable: false, 
        evented: false,
        scaleX: canvas.width / bg.width, 
        scaleY: canvas.height / bg.height 
      });
      canvas.backgroundImage = bg;
    }

    for (const item of parsedTemplate.layout || []) {
      const left = (item.x / 100) * canvas.width;
      const top = (item.y / 100) * canvas.height;

      if (item.type === "text") {
        let extraStyles = {};
        try {
          let srcStr = item.Src ?? item.src;
          if (srcStr) {
            const jsonStart = srcStr.indexOf("{");
            if (jsonStart !== -1) {
              srcStr = srcStr.substring(jsonStart);
              extraStyles = JSON.parse(srcStr);
            }
          }
        } catch (error) {
          extraStyles = {};
        }

        const textOptions = {
          left,
          top,
          fontSize: item.fontSize || 16,
          fontFamily: item.fontFamily || "Arial",
          fontWeight: item.fontWeight || extraStyles.fontWeight || "normal",
          fontStyle: item.fontStyle || extraStyles.fontStyle || "normal",
          underline: item.underline !== undefined ? item.underline : extraStyles.underline || false,
          fill: item.color || "#000",
          angle: item.angle || 0,
          textAlign: item.textAlign || "left",
          originX: item.OriginX || item.originX || "center",
          originY: item.OriginY || item.originY || "center",
          ...((item.styles || extraStyles.styles) && { styles: item.styles || extraStyles.styles }),
          selectable: false,
          evented: false,
        };

        const hasWidth = item.width || item.Width;
        if (hasWidth) {
          textOptions.width = hasWidth;
          canvas.add(new Textbox(item.text || "", textOptions));
        } else {
          canvas.add(new Text(item.text || "", textOptions));
        }
      }

      if (item.type === "image" || item.type === "signature" || item.type === "qr") {
        if (!item.src) continue;
        const imageUrl = getCorsUrl(item.src);
        const img = await FabricImage.fromURL(imageUrl, { crossOrigin: "anonymous" });
        const originX = item.originX || "left";
        const originY = item.originY || "top";
        img.set({ left, top, originX, originY, angle: item.angle || 0, selectable: false, evented: false });

        const targetWidth = item.width ? (item.width / canvas.width) * canvas.width : (item.scaleX || 0.5) * 0.32 * canvas.width;
        if (item.width) {
          img.scaleToWidth(targetWidth);
          if (item.height) img.scaleY = (item.height / img.getScaledHeight()) * img.scaleY;
        } else {
          img.scaleToWidth(targetWidth);
        }
        canvas.add(img);
      }

      if (item.type === "shape" && item.shapeType === "line") {
        const x1 = item.X1 ?? item.x1 ?? 0;
        const y1 = item.Y1 ?? item.y1 ?? 0;
        const x2 = item.X2 ?? item.x2 ?? 100;
        const y2 = item.Y2 ?? item.y2 ?? 100;
        const ox = (item.OriginX ?? item.originX) || "center";
        const oy = (item.OriginY ?? item.originY) || "center";
        const shape = new Line([x1, y1, x2, y2], {
          left, top, originX: ox, originY: oy, stroke: item.stroke || "#000", strokeWidth: item.strokeWidth || 2,
          strokeDashArray: item.dash || [], selectable: false, evented: false,
        });
        shape.set({ scaleX: item.scaleX || 1, scaleY: item.scaleY || 1, angle: item.angle || 0 });
        canvas.add(shape);
      }
    }

    await document.fonts.ready;
    canvas.renderAll();

    const dataURL = canvas.toDataURL({ format: "png", quality: 1, multiplier: 2 });
    const response = await fetch(dataURL);
    const blob = await response.blob();
    return new File([blob], fileName, { type: "image/png" });
  } catch (error) {
    return null;
  } finally {
    canvas.dispose();
  }
};
